import { codeableConcept } from '@seen/core/codeableConcept'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import { resolveId } from '@seen/core/references'
import { Button, cn } from '@seen/ui'
import { IconChevronDown } from '@tabler/icons-react'
import { type JSX, useEffect, useRef, useState } from 'react'
import { referenceNoun } from '#src/components/reference-picker/loadReferenceOptions.ts'
import { answerChipClass } from '#src/components/rich-text-editor/ObservationNode/observationChip.ts'
import { ObservationLastChip } from '#src/components/rich-text-editor/ObservationNode/ObservationHistory.tsx'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import { ObservationNoteSlot } from '#src/components/rich-text-editor/ObservationNode/ObservationNoteSlot.tsx'
import {
  ObservationReferencePicker,
  type ReferenceAnswer,
} from '#src/components/rich-text-editor/ObservationNode/ObservationReferencePicker.tsx'
import { ObservationRichTextEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationRichTextEditor.tsx'
import { CHOOSE_PARTICIPANT } from '#src/components/rich-text-editor/ObservationNode/observationSubject.ts'
import type {
  ObservationCaptureViewProps,
  ObservationNodeAttributes,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useNodeRecord } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useObservationHistory } from '#src/hooks/useObservationHistory.ts'

export function ObservationNodeReferenceInput({
  capture,
  ...props
}: ObservationCaptureViewProps): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  invariant(attributes.scaleType === 'Ref', 'scaleType must be Ref')
  invariant(
    attributes.referenceResourceType,
    'referenceResourceType is required'
  )

  const { participant, noParticipant } = capture

  const practitioner = useCurrentPractitioner()
  const { create } = useNodeRecord()

  const isReadOnly = useIsReadOnly()
  // Answered only where the note can be changed: not in a signed or
  // posted note, nor for a read-only role.
  const locked = isReadOnly || noParticipant || !capture.editable
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)

  const [lastObservation] = useObservationHistory({
    participant: inlineCapture ? participant : undefined,
    coding: { system: attributes.system, code: attributes.code },
    limit: 1,
  })

  const lastFocus = lastObservation?.focusReference

  const [loading, setLoading] = useState(false)

  const [note, setNote] = useState('')

  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (attributes.autofocus && buttonRef.current) {
      buttonRef.current.focus()
      props.updateAttributes({ autofocus: false })
    }
  }, [attributes.autofocus, props])

  const handleSelect = async ({
    reference,
    display,
  }: ReferenceAnswer): Promise<void> => {
    setLoading(true)
    await create(
      ExtendedObservation.fromFocusReference({
        reference,
        display,
        code: codeableConcept({
          code: attributes.code,
          system: attributes.system,
          display: attributes.codingDisplay,
        }),
        participant,
        practitioner: practitioner.reference,
        note,
        status: capture.recordStatus,
      })
    )
    setLoading(false)
  }

  return (
    <ObservationNodeFrame
      attributes={attributes}
      updateAttributes={props.updateAttributes}
      blockedReason={noParticipant ? CHOOSE_PARTICIPANT : undefined}
      participant={participant}
    >
      <ObservationReferencePicker
        resourceType={attributes.referenceResourceType}
        patientId={participant ? resolveId(participant) : undefined}
        onSelect={handleSelect}
      >
        <Button
          ref={buttonRef}
          variant="unstyled"
          size="unstyled"
          disabled={loading || locked}
          className={answerChipClass()}
        >
          Select {referenceNoun(attributes.referenceResourceType)}…
          <IconChevronDown className="size-3" />
        </Button>
      </ObservationReferencePicker>
      <ObservationLastChip
        observation={lastObservation}
        onRepeat={
          lastFocus?.reference && !locked
            ? () =>
                void handleSelect({
                  reference: lastFocus.reference ?? '',
                  display: lastFocus.display ?? '',
                })
            : undefined
        }
        disabled={loading || locked}
      />
      <ObservationNoteSlot note={note}>
        &nbsp;&nbsp;
        <div
          className="mr-[0.1875rem]" // Accounts for the shift below.
        />
        <ObservationRichTextEditor
          placeholder="Notes…"
          disabled={locked}
          singleLine={inlineCapture}
          onChange={setNote}
          className={cn(
            'grow', // So that the editor can grow to fill the available horizontal space when the content goes onto multiple lines.
            '-ml-[0.1875rem]' // For visual alignment when the content goes onto multiple lines.
          )}
        />
      </ObservationNoteSlot>
    </ObservationNodeFrame>
  )
}
